import { createContext, useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { tooltipsApi } from '@/api/endpoints/admin';
import { useAuthStore } from '@/stores/auth.store';
import type { TooltipEntry } from '@/types/admin';

interface TooltipContextValue {
  byKey: Map<string, TooltipEntry>;
  bySelector: TooltipEntry[];
  tours: Map<string, TooltipEntry[]>;
  isLoading: boolean;
}

const TooltipContext = createContext<TooltipContextValue | null>(null);

/**
 * Loads all active tooltips once per session and indexes them by key,
 * selector, and tour name. The tooltip runtime reads from this context
 * via useTooltip(key) or useTour(name) — no per-tooltip network calls.
 */
export function TooltipProvider({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { data = [], isLoading } = useQuery({
    queryKey: ['tooltips', 'active'],
    queryFn: () => tooltipsApi.list(),
    staleTime: 5 * 60 * 1000,
    retry: false,
    // Tooltips are an authenticated read. Gate on isAuthenticated (not merely
    // hasToken) so a stale JWT in localStorage during boot does not 401 and
    // trip the global redirect-to-login interceptor before auth init finishes.
    enabled: isAuthenticated,
  });

  const value = useMemo<TooltipContextValue>(() => {
    const byKey = new Map<string, TooltipEntry>();
    const bySelector: TooltipEntry[] = [];
    const tours = new Map<string, TooltipEntry[]>();

    for (const t of data) {
      if (!t.isActive) continue;
      byKey.set(t.key, t);
      if (t.targetSelector) bySelector.push(t);
      if (t.tourName) {
        if (!tours.has(t.tourName)) tours.set(t.tourName, []);
        tours.get(t.tourName)!.push(t);
      }
    }
    for (const [, list] of tours) {
      list.sort((a, b) => (a.tourOrder ?? 0) - (b.tourOrder ?? 0));
    }
    return { byKey, bySelector, tours, isLoading };
  }, [data, isLoading]);

  return <TooltipContext.Provider value={value}>{children}</TooltipContext.Provider>;
}

export function useTooltip(key: string): TooltipEntry | undefined {
  const ctx = useContext(TooltipContext);
  return ctx?.byKey.get(key);
}

export function useTour(name: string): TooltipEntry[] {
  const ctx = useContext(TooltipContext);
  return ctx?.tours.get(name) ?? [];
}

export function useTooltipContext(): TooltipContextValue {
  const ctx = useContext(TooltipContext);
  if (!ctx) {
    return { byKey: new Map(), bySelector: [], tours: new Map(), isLoading: false };
  }
  return ctx;
}
